<template>
  <span v-if="geolocation">
    <v-tooltip top v-if="geolocation.error">
      <template v-slot:activator="{ on, attrs }">
        <v-icon
          small
          v-on="on"
          class="gray--text mb-1"
        >
          location_off
        </v-icon>
      </template>
      <span>Location {{ geolocation.error }}: {{ geolocation.errorMessage }}</span>
    </v-tooltip>
    <v-tooltip top v-else-if="hasValidCoordinates">
      <template v-slot:activator="{ on, attrs }">
        <v-icon
          small
          v-on="on"
          :class="getLocationIconClass()"
          class="mb-1"
        >
          {{ getLocationIcon() }}
        </v-icon>
      </template>
      <div>
        <strong>Location:</strong><br/>
        <span v-if="address">{{ address }}</span>
        <span v-else>{{ latitude.toFixed(6) }}, {{ longitude.toFixed(6) }}</span>
        <br/>
        <span v-if="accuracy" class="caption">Accuracy: ±{{ accuracy.toFixed(0) }}m</span>
        <br/>
        <span v-if="distance !== null">
          <strong>Distance from site:</strong> {{ distance }}
        </span>
        <span v-else-if="!jobSiteLocation">
          <strong>Distance from site:</strong> Unknown (job site has no coordinates)
        </span>
      </div>
    </v-tooltip>
  </span>
</template>

<script>
import fb from "@/main";

export default {
  props: {
    geolocation: {
      type: Object,
      default: null,
    },
    jobSiteLocation: {
      type: Object,
      default: null,
    },
    distanceMeters: {
      type: Number,
      default: null,
    },
    locationType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      address: null,
      distance: null,
      loading: false,
    };
  },
  computed: {
    // Extract latitude/longitude from either GeoPoint or legacy format
    latitude() {
      if (!this.geolocation) return null;
      return this.geolocation.latitude;
    },
    longitude() {
      if (!this.geolocation) return null;
      return this.geolocation.longitude;
    },
    accuracy() {
      if (!this.geolocation) return null;
      return this.geolocation.accuracy;
    },
    hasValidCoordinates() {
      return this.latitude !== null &&
             this.latitude !== undefined &&
             this.longitude !== null &&
             this.longitude !== undefined;
    },
  },
  watch: {
    geolocation: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.hasValidCoordinates) {
          this.fetchAddressFromCache();
        }
      },
    },
    distanceMeters: {
      immediate: true,
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.formatDistance(newVal);
        }
      },
    },
  },
  methods: {
    async fetchAddressFromCache() {
      if (this.loading) return;

      try {
        this.loading = true;
        const latitude = this.latitude;
        const longitude = this.longitude;

        // Create cache key matching the cloud function
        const cacheKey = `${latitude},${longitude}`;

        // Try to fetch from geocodeCache collection
        const cacheDoc = await fb.db
          .collection("geocodeCache")
          .doc(cacheKey)
          .get();

        if (cacheDoc.exists) {
          // Use cached address
          const cached = cacheDoc.data();
          this.address = cached.address;
        }
        // If not in cache, leave address as null - coordinates will be shown instead
      } catch (error) {
        console.warn('Error fetching address from cache:', error);
        // Silently fail - coordinates will be shown instead
      } finally {
        this.loading = false;
      }
    },
    formatDistance(distanceMeters) {
      // Convert to appropriate unit
      if (distanceMeters < 1000) {
        this.distance = `${Math.round(distanceMeters)}m`;
      } else {
        const miles = distanceMeters / 1609.34;
        this.distance = `${miles.toFixed(2)} miles`;
      }
    },
    getLocationIcon() {
      if (!this.jobSiteLocation || this.distance === null) {
        return 'location_on';
      }

      // Use pre-calculated location type
      if (this.locationType === 'green' || this.locationType === 'orange') {
        return 'location_on';
      }
      // Red or error
      return 'wrong_location';
    },
    getLocationIconClass() {
      if (!this.jobSiteLocation || this.distance === null) {
        return 'blue--text';
      }

      // Use pre-calculated location type for color
      if (this.locationType === 'green') {
        return 'green--text';
      }
      if (this.locationType === 'orange') {
        return 'orange--text';
      }
      if (this.locationType === 'red') {
        return 'red--text';
      }
      // Default fallback
      return 'blue--text';
    },
  },
};
</script>
